import { LoadError } from '@/components/LoadError';
import { GoalTrend } from '@/components/GoalTrend';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { goalProgressModes, type GoalProgressMode } from '@horizon/shared';
import { Progress } from '@/components/Progress';
import { GoalMeasure } from '@/components/GoalMeasure';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/toast-context';
import { useArchiveGoal, useCreateProject, useGoal, useUpdateGoal } from '@/lib/queries';

export default function GoalPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const toast = useToast();
  const query = useGoal(id);
  const updateGoal = useUpdateGoal();
  const archiveGoal = useArchiveGoal();
  const createProject = useCreateProject();
  const [projectError, setProjectError] = useState(false);
  const [projectTitle, setProjectTitle] = useState('');

  if (query.isError) return <LoadError retry={() => query.refetch()} />;
  if (!query.data) return <p className="text-sm text-muted">{t('common.loading')}</p>;

  const { goal, projects, snapshots } = query.data;

  const setMode = (mode: GoalProgressMode) => {
    if (mode === goal.progressMode) return;
    // Переход в режим ключевого результата требует целевого значения в том же
    // запросе, иначе цель окажется в состоянии, которое нельзя посчитать.
    const patch: Record<string, unknown> =
      mode === 'metric'
        ? {
            progressMode: mode,
            metricTarget: goal.metricTarget ?? 100,
            metricCurrent: goal.metricCurrent ?? 0,
          }
        : mode === 'manual'
          ? {
              progressMode: mode,
              manualProgress: goal.manualProgress ?? Math.round(goal.progressPercent ?? 0),
            }
          : { progressMode: mode };
    updateGoal.mutate({ id: goal.id, patch });
  };

  return (
    <section className="flex flex-col gap-6">
      <div>
        <Link
          to="/goals"
          className="inline-flex min-h-11 min-w-11 items-center text-sm text-muted hover:text-ink"
        >
          ← {t('goals.title')}
        </Link>
        <h1 className="mt-2 flex items-baseline gap-2 font-serif text-2xl font-semibold">
          {goal.emoji && <span aria-hidden="true">{goal.emoji}</span>}
          {goal.title}
        </h1>
        <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted">
          <span>{t(`goal.horizon.${goal.horizon}`)}</span>
          {goal.targetDate && (
            <span>{t('goal.until', { date: formatDate(goal.targetDate, i18n.language) })}</span>
          )}
          <span className={goal.pace === 'behind' ? 'text-near' : undefined}>
            {t(`goal.pace.${goal.pace}`)}
          </span>
        </p>
      </div>

      {goal.note && (
        <p className="max-w-prose rounded-card border border-line bg-surface px-4 py-3 text-sm text-ink-2">
          {goal.note}
        </p>
      )}

      <div className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4">
        <Progress
          percent={goal.progressPercent}
          label={t('goal.progressOf', { title: goal.title })}
        />
        <p className="text-xs text-muted">
          <GoalMeasure goal={goal} />
        </p>

        <div role="group" aria-label={t('goal.progressMode')} className="flex flex-wrap gap-2">
          {goalProgressModes.map((mode) => (
            <Button
              key={mode}
              size="sm"
              variant={goal.progressMode === mode ? 'primary' : 'outline'}
              aria-pressed={goal.progressMode === mode}
              onClick={() => setMode(mode)}
            >
              {t(`goal.mode.${mode}`)}
            </Button>
          ))}
        </div>

        {goal.progressMode === 'metric' && (
          <label className="flex max-w-xs items-center gap-2 text-sm">
            {t('goal.metricCurrent')}
            <input
              type="number"
              defaultValue={goal.metricCurrent ?? 0}
              onBlur={(e) =>
                updateGoal.mutate({
                  id: goal.id,
                  patch: { metricCurrent: Number(e.target.value), metricTarget: goal.metricTarget },
                })
              }
              className="min-h-11 w-24 rounded-lg border border-line bg-bg px-3"
            />
            <span className="text-muted">/ {goal.metricTarget}</span>
          </label>
        )}

        {goal.progressMode === 'manual' && (
          <label className="flex max-w-xs items-center gap-3 text-sm">
            {t('goal.manualProgress')}
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              defaultValue={goal.manualProgress ?? 0}
              onChange={(e) =>
                updateGoal.mutate({
                  id: goal.id,
                  patch: { manualProgress: Number(e.target.value) },
                })
              }
              className="flex-1"
            />
          </label>
        )}
      </div>

      <section className="flex flex-col gap-3" aria-label={t('goal.projects')}>
        <h2 className="text-sm font-medium text-ink-2">{t('goal.projects')}</h2>
        {projects.length === 0 && <p className="text-sm text-muted">{t('goal.noProjects')}</p>}
        <ul className="flex flex-col gap-2">
          {projects.map((project) => (
            <li key={project.id} className="rounded-card border border-line bg-surface">
              <Link
                to={`/projects/${project.id}`}
                className="flex flex-col gap-2 p-4 hover:border-accent"
              >
                <span className="font-medium">{project.title}</span>
                <Progress
                  percent={
                    project.taskCounts.total === 0
                      ? null
                      : (project.taskCounts.done / project.taskCounts.total) * 100
                  }
                  label={t('goal.progressOf', { title: project.title })}
                  tone="project"
                />
                <span className="text-xs text-muted">
                  {t('goal.tasksDone', {
                    done: project.taskCounts.done,
                    total: project.taskCounts.total,
                  })}
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <form
          className="flex flex-wrap gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!projectTitle.trim()) return;
            setProjectError(false);
            try {
              await createProject.mutateAsync({ title: projectTitle.trim(), goalId: goal.id });
              setProjectTitle('');
            } catch {
              setProjectError(true);
            }
          }}
        >
          <input
            disabled={createProject.isPending}
            value={projectTitle}
            onChange={(e) => setProjectTitle(e.target.value)}
            placeholder={t('goal.newProject')}
            aria-label={t('goal.newProject')}
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 outline-none focus:border-accent"
          />
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={!projectTitle.trim() || createProject.isPending}
          >
            {t('common.create')}
          </Button>
        </form>
      </section>

      {projectError && <p role="alert">{t('polish.draftError')}</p>}
      <GoalTrend goalId={goal.id} />
      {snapshots.length > 0 && (
        <section className="flex flex-col gap-2" aria-label={t('goal.history')}>
          <h2 className="text-sm font-medium text-ink-2">{t('goal.history')}</h2>
          <ul className="flex flex-col gap-1 text-sm text-muted">
            {snapshots.map((snapshot) => (
              <li key={snapshot.capturedAt} className="flex justify-between gap-4">
                <span>{formatDate(snapshot.capturedAt, i18n.language)}</span>
                <span className="tabular-nums">{Math.round(snapshot.progressPercent)}%</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div>
        <Button
          size="sm"
          onClick={() => {
            archiveGoal.mutate({ id: goal.id, archived: !goal.archivedAt });
            toast.show({
              message: goal.archivedAt ? t('archive.restored') : t('archive.archivedGoal'),
              actionLabel: t('common.undo'),
              onAction: () => archiveGoal.mutate({ id: goal.id, archived: !!goal.archivedAt }),
            });
          }}
        >
          {goal.archivedAt ? t('archive.restore') : t('archive.archive')}
        </Button>
      </div>
    </section>
  );
}

function formatDate(date: string, locale: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}
